import { useContext, useEffect, useState } from 'react'
import {
  MemoContext,
  SELECT_DTC_MEMO,
  SET_UPDATED_DTC_MEMO,
  SHOW_DTC_MEMO_POPUP,
} from '../context/context'
import SelectInput from '../../../common/SelectInput'
import InputText from '../../../common/InputText'
import TextArea from '../../../common/TextArea'
import Button from '../../../common/Button'
import useDebouncedValue from '../../../../utils/common_function'
import { useParams } from 'react-router-dom'
import { getRequest, postRequest, putRequest } from '../../../../services/base'
import { NavContext } from '../../../../context/global_context'
import { Flex, Form, Modal, Space, Typography } from 'antd'
import { prettyPrintFormData } from '../../../../helpers/utils'
import EllipsisTooltip from '../../../common/EllipsisTooltip'
import { useIntl } from 'react-intl'

const DtcMemoFormPopup = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(MemoContext)
  const [navState, navDispatch] = useContext(NavContext)

  const [options, setOptions] = useState([])
  const [searchFailed, setSearchFailed] = useState(false)
  const [searchDtc, setSearchDtc] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchDtc, 1000)
  const [form] = Form.useForm()

  useEffect(() => {
    getDtcsData('')
  }, [])

  useEffect(() => {
    getDtcsData(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  const getDtcsData = async (search = '') => {
    if (!state?.selected_memo?.id) {
      let res = await getRequest(
        `/api/dtcs/?vehicle=${vehicle}&search=${search}&limit=5`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed(true)
        setOptions([])
        return
      }
      setSearchFailed(false)

      const dtcData = res.results.map((dtc, index) => {
        return {
          ...dtc,
          value: dtc.id,
        }
      })

      setOptions(dtcData)
    } else {
      const selectedOption = {
        ...state?.selected_memo.dtc_obj,
        value: state?.selected_memo.dtc_obj.id,
      }

      setOptions([selectedOption])
    }
  }

  const dtcFilterOptions = (option, searchText) => {
    if (
      option.name.toLowerCase().includes(searchText.toLowerCase()) ||
      option.description.toLowerCase().includes(searchText.toLowerCase()) ||
      option.ecu.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true
    } else {
      return false
    }
  }

  const closeCreateMemoPopup = () => {
    dispatch({
      type: SELECT_DTC_MEMO,
      data: { selected_memo: {}, show_memo_popup: false },
    })
  }

  const saveDTCMemos = async (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, value ?? '')
    })
    formData.append('vehicle', vehicle)

    let res = null
    if (state?.selected_memo?.id) {
      res = await putRequest(
        `/api/dtc_memos/${state?.selected_memo?.id}/?vehicle=${vehicle}`,
        formData
      )
      if (res?.status) {
        dispatch({
          type: SET_UPDATED_DTC_MEMO,
          data: { ...res.data, index: state?.selected_memo?.index },
        })
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'memos.saveSuccess' }),
          },
        })
        closeCreateMemoPopup()
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'memos.saveError' }),
          },
        })
      }
    } else {
      res = await postRequest(`/api/dtc_memos/?vehicle=${vehicle}`, formData)
      props.getListData()
      closeCreateMemoPopup()
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'memos.saveSuccess' }),
        },
      })
    }
  }

  const getInitialValues = () => {
    return {
      is_create: state?.selected_memo?.id,
      id: state?.selected_memo?.id,
      dtc: state?.selected_memo.dtc_obj?.id ?? null,
      memo: state?.selected_memo?.memo ?? null,
      short_desc: state?.selected_memo?.short_desc ?? null,
    }
  }

  useEffect(() => {
    if (!form || !state || options?.length === 0) return

    form.setFieldsValue(getInitialValues())
  }, [form, state.show_memo_popup, options])

  return (
    <Modal
      open={state.show_memo_popup}
      title={intl.formatMessage({ id: 'memos.dtcMemos' })}
      width="60%"
      className="create-new-memo-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={() => {
        dispatch({
          type: SELECT_DTC_MEMO,
          data: { selected_memo: {}, show_memo_popup: false },
        })
      }}
    >
      <div className="container">
        <Form
          id="create-new-memo-form"
          name="create-new-memo-form"
          form={form}
          layout="vertical"
          onFinish={saveDTCMemos}
        >
          <InputText
            name="is_create"
            hidden={true}
            isRequired={false}
          />

          <InputText
            name="import_file"
            hidden={true}
            isRequired={false}
            value={null}
          />

          {state?.selected_memo?.id && (
            <InputText
              name="id"
              hidden={true}
              isRequired={false}
              value={state?.selected_memo?.id}
            />
          )}

          <Flex vertical gap={16}>
            <SelectInput
              name="dtc"
              label={intl.formatMessage({ id: 'memos.dtc' })}
              options={options}
              placeholder={intl.formatMessage({ id: 'memos.selectDtc' })}
              isRequired={true}
              allowClear={true}
              showSearch={true}
              disabled={state?.selected_memo?.id ? true : false}
              wrapperClass="select-dtc-wrapper"
              selectClass="select-dtc-multiline"
              selectPopupClass="select-dtc-popup"
              onChange={(option) => {
                dispatch({
                  type: SELECT_DTC_MEMO,
                  data: {
                    selected_memo: {
                      ...state?.selected_memo,
                      dtc_obj: option,
                    },
                    show_memo_popup: true,
                  },
                })
              }}
              onInputChange={(value) => {
                if (value) {
                  setSearchDtc(value)
                }
              }}
              notFoundContent={
                searchFailed ? (
                  <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                    {intl.formatMessage({ id: 'generic.searchFailed' })}
                  </Typography.Text>
                ) : undefined
              }
              filterOption={(input, option) => {
                return dtcFilterOptions(option, input)
              }}
              formatOption={(option) => (
                <>
                  <div className="option-label">{option.name}</div>
                  <div className="option-description">{option.description}</div>
                </>
              )}
              formatLabel={(option) => {
                return (
                  <div className="dtc-selected-label">
                    <div className="option-label">
                      {option.name} ({option.ecu})
                    </div>
                    <div className="option-description">
                      {option.description}
                    </div>
                  </div>
                )
              }}
            />

            <Flex align="flex-start" gap={16}>
              <TextArea
                name="memo"
                label={intl.formatMessage({ id: 'memos.memo' })}
                isRequired={false}
                wrapperClass="memo-name"
              />

              <EllipsisTooltip
                className="memo-description"
                selector="input"
                value={state?.selected_memo?.short_desc ?? null}
              >
                <InputText
                  name="short_desc"
                  label={intl.formatMessage({ id: 'memos.shortDescription' })}
                  wrapperClass=""
                  isRequired={true}
                  className={
                    state?.selected_memo?.id ? 'ellipsis no-pointer-events' : ''
                  }
                  disabled={state?.selected_memo?.id ? true : false}
                  maxLength={128}
                />
              </EllipsisTooltip>
            </Flex>

            <Flex justify='flex-end' style={{padding: '16px 0 0'}}>
              <Button htmlType="submit" type="primary">
                {intl.formatMessage({ id: 'memos.saveDtcMemos' })}
              </Button>
            </Flex>
          </Flex>
        </Form>
      </div>
    </Modal>
  )
}

export default DtcMemoFormPopup
